/*
 * @Author: lizhigang
 * @Date: 2025-12-08
 * @Company: www.smm.cn
 * @Description: Google 登录 Hook
 */
"use client"

import { setClientCookie } from "@repo/shared"
import http from "@repo/ui/utils"
import Script from "next/script"
import { useCallback, useEffect, useRef, useState } from "react"
import { message } from "antd"

import { COOKIE_DEFAULT_KEY, COOKIE_TEMPORARY_KEY } from "../const"
import { fetchNecessary } from "../utils"

interface UseGoogleLoginOptions {
  /**
   * 登录成功回调
   */
  onSuccess?: (msg?: string) => void
  /**
   * 登录失败回调
   */
  onError?: (error: string) => void
  /**
   * 当前语言
   */
  locale?: string
  /**
   * 个人信息补全页面 URL
   */
  profileCompleteUrl?: string
  /**
   * Google Client ID
   */
  clientId?: string
}

interface UseGoogleLoginReturn {
  /**
   * Google 登录按钮的 ref
   */
  googleLoginRef: React.RefObject<HTMLDivElement | null>
  /**
   * Google 脚本是否已加载
   */
  isScriptLoaded: boolean
  /**
   * Google Script 组件
   */
  GoogleScript: React.ComponentType
}

const DEFAULT_CLIENT_ID = "720075917606-473tlerbifpvqjrmc55v73h6bvj8bcpr.apps.googleusercontent.com"

// 全局变量：防止多个组件实例或重新渲染导致重复初始化
let globalGoogleAuthInitialized = false
let globalGoogleAuthInitializing = false
let globalGoogleAuthInstance: any = null
// 记录已绑定点击处理器的元素，防止重复绑定
const boundElements = new WeakSet<HTMLElement>()

/**
 * Google 登录 Hook
 */
export const useGoogleLogin = (options: UseGoogleLoginOptions = {}): UseGoogleLoginReturn => {
  const { onSuccess, onError, locale = "en", profileCompleteUrl, clientId = DEFAULT_CLIENT_ID } = options

  const googleLoginRef = useRef<HTMLDivElement | null>(null)
  const [isScriptLoaded, setIsScriptLoaded] = useState(false)
  // 防止重复请求
  const isRequestingRef = useRef(false)

  /**
   * 获取第三方 token
   */
  const getThirdToken = useCallback(
    async (openid: string, access_token: string, email?: string) => {
      // 防止重复请求
      if (isRequestingRef.current) {
        console.warn("Google login request already in progress")
        return
      }
      isRequestingRef.current = true

      try {
        const params = new URLSearchParams({
          openid,
          access_token,
          email: email || "",
          source: "metal.multi",
          referrer: window.location.href || "",
        })
        const response = await http.get<
          { openid: string; access_token: string; email?: string; source?: string; referrer?: string },
          any
        >(`/usercenter/en/google_code_to_user_info_v2?${params}`, {
          showOriginResponse: true,
          getLang: () => locale,
        })
        if (response?.code === 0) {
          const { data } = response
          const isNecessary = await fetchNecessary(data.token, locale)
          if (isNecessary) {
            // sessionStorage.setItem(COOKIE_DEFAULT_KEY, data.token)
            setClientCookie(COOKIE_TEMPORARY_KEY, data.token, undefined, ".metal.com")
            // 先跳转，再在后台发送位置信息
            if (profileCompleteUrl) {
              console.info("跳转信息补全")
              window.location.href = profileCompleteUrl
            }
            // sendLatLng(locale)
          } else {
            // 设置 session cookie
            await setClientCookie(COOKIE_DEFAULT_KEY, data.token, undefined, ".metal.com")
            // 先触发成功回调，再刷新页面
            onSuccess?.(response.msg)
            // 在后台发送位置信息，不等待完成
            // sendLatLng(locale)
          }
        } else {
          throw new Error(response?.msg)
        }
      } catch (errorInfo) {
        const msg = typeof errorInfo === "string" ? errorInfo : (errorInfo as Error).message
        message.error({
          className: "sign-in-api-toast",
          content: msg || "unknown error",
        })
        onError?.(msg)
      } finally {
        isRequestingRef.current = false
      }
    },
    [locale, profileCompleteUrl, onSuccess, onError]
  )

  /**
   * 绑定点击处理器
   */
  const attachClickHandler = useCallback(
    (auth2: any) => {
      if (!googleLoginRef.current) {
        console.warn("Google login button ref not available")
        return
      }

      // 防止重复绑定点击处理器到同一个元素
      if (boundElements.has(googleLoginRef.current)) {
        console.log("Click handler already attached to this element")
        return
      }

      boundElements.add(googleLoginRef.current)
      console.log("Attaching click handler to element")

      auth2.attachClickHandler(
        googleLoginRef.current,
        {},
        (res: any) => {
          const basic = res.getBasicProfile()
          const email = basic.getEmail()
          const openid = basic.getId()
          const { access_token } = res.getAuthResponse()
          getThirdToken(openid, access_token, email)
        },
        (error: any) => {
          console.error("Google sign in failed:", error)
          // 忽略用户关闭弹窗和重复请求的错误
          if (error?.error !== "popup_closed_by_user") {
            onError?.(`Google sign in failed: ${error?.error || "unknown"}`)
          }
        }
      )
    },
    [getThirdToken, onError]
  )

  /**
   * Google 登录初始化
   */
  const googleInit = useCallback(() => {
    const w = window as any

    if (!w.gapi) {
      console.error("Google API not loaded")
      return
    }

    // 如果已经初始化完成，直接使用现有实例绑定点击事件
    if (globalGoogleAuthInitialized && globalGoogleAuthInstance) {
      console.log("Reusing existing Google auth instance")
      attachClickHandler(globalGoogleAuthInstance)
      return
    }

    // 如果正在初始化中，等待初始化完成
    if (globalGoogleAuthInitializing) {
      console.log("Google auth is initializing, waiting...")
      const checkInterval = setInterval(() => {
        if (globalGoogleAuthInitialized && globalGoogleAuthInstance) {
          clearInterval(checkInterval)
          attachClickHandler(globalGoogleAuthInstance)
        }
      }, 100)
      // 5秒超时
      setTimeout(() => clearInterval(checkInterval), 5000)
      return
    }

    // 标记正在初始化
    globalGoogleAuthInitializing = true

    w.gapi.load("auth2", () => {
      // 再次检查是否已经有实例（可能在 load 期间被其他地方初始化）
      const existingInstance = w.gapi.auth2.getAuthInstance()
      if (existingInstance) {
        console.log("Found existing Google auth instance")
        globalGoogleAuthInstance = existingInstance
        globalGoogleAuthInitialized = true
        globalGoogleAuthInitializing = false
        attachClickHandler(existingInstance)
        return
      }

      const auth2 = w.gapi.auth2.init({
        client_id: clientId,
        scope: "profile email openid",
      })

      auth2.then(
        () => {
          console.log("Google auth2 initialized successfully")
          globalGoogleAuthInstance = auth2
          globalGoogleAuthInitialized = true
          globalGoogleAuthInitializing = false
          attachClickHandler(auth2)
        },
        (error: any) => {
          console.error("Google auth2 init failed:", error)
          globalGoogleAuthInitializing = false
          onError?.("Google auth2 init failed")
        }
      )
    })
  }, [clientId, attachClickHandler, onError])

  /**
   * Google Script 组件
   */
  const GoogleScript = useCallback(() => {
    return (
      <Script
        id="google-platform-script"
        src="https://apis.google.com/js/platform.js"
        strategy="lazyOnload"
        onError={() => {
          console.log("Load Google script failed")
          setIsScriptLoaded(false)
          onError?.("Load Google script failed")
        }}
        onLoad={() => {
          console.log("Load Google script success")
          setIsScriptLoaded(true)
          googleInit()
        }}
      />
    )
  }, [googleInit, onError])

  // 当组件挂载时，检查 Google 脚本是否已经加载（弹窗重新打开的情况）
  // 如果已加载，需要重新绑定点击事件到新的 DOM 元素
  useEffect(() => {
    const w = window as any
    if (w.gapi && googleLoginRef.current) {
      console.log("Google script already loaded, reinitializing...")
      setIsScriptLoaded(true)
      googleInit()
    }
  }, [googleInit])

  return {
    googleLoginRef,
    isScriptLoaded,
    GoogleScript,
  }
}
